// RouteLink.js
import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import PrivateRoute from "./Utilities/PrivateRoute";

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("./Pages/Login/LoginPage"));
const DashboardLayout = React.lazy(() => import("./Layout/DashboardLayout"));
const DashboardHome = React.lazy(
  () => import("./Pages/Dashboard/DashboardHome"),
);

const Category = React.lazy(() => import("./Pages/Category/Category"));
const AddCategory = React.lazy(() => import("./Pages/Category/AddCategory"));

const Brand = React.lazy(() => import("./Pages/Brand/Brand"));
const AddBrand = React.lazy(() => import("./Pages/Brand/AddBrand"));

const SubCategory = React.lazy(() => import("./Pages/SubCategory/SubCategory"));
const AddSubCategory = React.lazy(() => import("./Pages/SubCategory/AddSubCategory"));

const Attribute = React.lazy(() => import("./Pages/Attribute/Attribute"));
const AddAttribute = React.lazy(() => import("./Pages/Attribute/AddAttribute"));

const Product = React.lazy(() => import("./Pages/Product/Product"));
const AddProduct = React.lazy(() => import("./Pages/Product/AddProduct"));

const TaxRate = React.lazy(() => import("./Pages/TaxRate/TaxRate"));
const AddTaxRate = React.lazy(() => import("./Pages/TaxRate/AddTaxRate"));

const Discount = React.lazy(() => import("./Pages/Discount/Discount"));
const AddDiscount = React.lazy(() => import("./Pages/Discount/AddDiscount"));

const Coupon = React.lazy(() => import("./Pages/Coupon/Coupon"));
const AddCoupon = React.lazy(() => import("./Pages/Coupon/AddCoupon"));

const ProductCombo = React.lazy(() =>
  import("./Pages/ProductCombo/ProductCombo")
);
const AddProductCombo = React.lazy(() =>
  import("./Pages/ProductCombo/AddProductCombo")
);

const StockIn = React.lazy(() => import("./Pages/StockIn/StockIn"));

const StockMovements = React.lazy(() => import("./Pages/StockMovements/StockMovements"));

const ProductCsv = React.lazy(() => import("./Pages/ProductCsv/ProductCsv"));

const Order = React.lazy(() => import("./Pages/Order/Order"));

const Review = React.lazy(() => import("./Pages/Review/Review"));

const Shipping = React.lazy(() => import("./Pages/Shipping/Shipping"));

const Return = React.lazy(() => import("./Pages/Return/Return"));

const Tickets = React.lazy(() => import("./Pages/Tickets/Tickets"));

const Customers = React.lazy(() => import("./Pages/Customers/Customers"));

const Banners = React.lazy(() => import("./Pages/Banners/Banners"));
const HomeBanners = React.lazy(() => import("./Pages/Banners/HomeBanners"));
const PromoBanners = React.lazy(() => import("./Pages/Banners/PromoBanners"));

const Testimonials = React.lazy(() => import("./Pages/Testimonials/Testimonials"));

const PaymentSettings = React.lazy(() => import("./Pages/PaymentSettings/PaymentSettings"));

const StoreSettings = React.lazy(() => import("./Pages/StoreSettings/StoreSettings"));

const SiteThemeSettings = React.lazy(() => import("./Pages/SiteTheme/SiteThemeSettings"));

const StaticPageEditor = React.lazy(() => import("./Pages/StaticPages/StaticPageEditor"));
const FooterLinks = React.lazy(() => import("./Pages/FooterLinks/FooterLinks"));
const ContactMessages = React.lazy(() => import("./Pages/ContactMessages/ContactMessages"));
const Subscribers = React.lazy(() => import("./Pages/Subscribers/Subscribers"));
const MyProfile = React.lazy(() => import("./Pages/Profile/MyProfile"));
const Users = React.lazy(() => import("./Pages/Users/Users"));
const UserPermissions = React.lazy(() => import("./Pages/UserPermissions/UserPermissions"));


const routes = [
  {
    path: "/login",
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PrivateRoute />
      </Suspense>
    ), // 🛡 Wrap protected routes here
    children: [
      {
        path: "/dashboard",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <DashboardLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <DashboardHome />
              </Suspense>
            ),
          },
         
          {
            path: "category",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Category />
              </Suspense>
            ),
          },
          {
            path: "categoryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddCategory />
              </Suspense>
            ),
          },
          //
          {
            path: "brand",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Brand />
              </Suspense>
            ),
          },
          {
            path: "brandadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddBrand />
              </Suspense>
            ),
          },
          //
          {
            path: "subcategory",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SubCategory />
              </Suspense>
            ),
          },
          {
            path: "subcategoryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddSubCategory />
              </Suspense>
            ),
          },
          //
          {
            path: "attribute",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Attribute />
              </Suspense>
            ),
          },
          {
            path: "attributeadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddAttribute />
              </Suspense>
            ),
          },
          //
          {
            path: "product",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Product />
              </Suspense>
            ),
          },
          {
            path: "productadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddProduct />
              </Suspense>
            ),
          },
          {
            path: "product-csv",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProductCsv />
              </Suspense>
            ),
          },
          //
          {
            path: "taxrate",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <TaxRate />
              </Suspense>
            ),
          },
          {
            path: "taxrateadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddTaxRate />
              </Suspense>
            ),
          },
          //
          {
            path: "discount",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Discount />
              </Suspense>
            ),
          },
          {
            path: "discountadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddDiscount />
              </Suspense>
            ),
          },
          //
          {
            path: "coupon",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Coupon />
              </Suspense>
            ),
          },
          {
            path: "couponadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddCoupon />
              </Suspense>
            ),
          },
          //
          {
            path: "productcombo",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProductCombo />
              </Suspense>
            ),
          },
          {
            path: "productcomboadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddProductCombo />
              </Suspense>
            ),
          },
          //
          {
            path: "stockin",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StockIn />
              </Suspense>
            ),
          },
          {
            path: "stockmovements",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StockMovements />
              </Suspense>
            ),
          },
          //
          {
            path: "order",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Order />
              </Suspense>
            ),
          },
          //
          {
            path: "review",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Review />
              </Suspense>
            ),
          },
          //
          {
            path: "shipping",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Shipping />
              </Suspense>
            ),
          },
          //
          {
            path: "return",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Return />
              </Suspense>
            ),
          },
          //
          {
            path: "tickets",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Tickets />
              </Suspense>
            ),
          },
          //
          {
            path: "customers",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Customers />
              </Suspense>
            ),
          },
          //
          {
            path: "banners",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Banners />
              </Suspense>
            ),
          },
          {
            path: "home-banners",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <HomeBanners />
              </Suspense>
            ),
          },
          {
            path: "promo-banners",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PromoBanners />
              </Suspense>
            ),
          },
          //
          {
            path: "testimonials",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Testimonials />
              </Suspense>
            ),
          },
          //
          {
            path: "payment-settings",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PaymentSettings />
              </Suspense>
            ),
          },
          //
          {
            path: "store-settings",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StoreSettings />
              </Suspense>
            ),
          },
          //
          {
            path: "site-theme",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SiteThemeSettings />
              </Suspense>
            ),
          },
          {
            path: "pages/:slug",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <StaticPageEditor />
              </Suspense>
            ),
          },
          {
            path: "footer-links",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <FooterLinks />
              </Suspense>
            ),
          },
          {
            path: "contact-messages",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ContactMessages />
              </Suspense>
            ),
          },
          {
            path: "subscribers",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Subscribers />
              </Suspense>
            ),
          },
          {
            path: "my-profile",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <MyProfile />
              </Suspense>
            ),
          },
          {
            path: "users",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Users />
              </Suspense>
            ),
          },
          {
            path: "user-permissions",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UserPermissions />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;

